import chalk from "chalk";
import { createInterface } from "node:readline";
import type { CaiConfig, DriftReport } from "./types.js";
import { runDriftCheck } from "./drift/index.js";
import { runAutoFix, printFixResult } from "./fix.js";
import { reportConsole } from "./reporter.js";
import { runSync } from "./sync/index.js";
import { runHealth, printHealth } from "./health.js";

function ask(question: string): Promise<boolean> {
  const rl = createInterface({ input: process.stdin, output: process.stdout });
  return new Promise((resolve) => {
    rl.question(question, (answer) => {
      rl.close();
      resolve(answer.trim().toLowerCase() !== "n");
    });
  });
}

function scoreColor(score: number) {
  return score >= 80 ? chalk.green : score >= 50 ? chalk.yellow : chalk.red;
}

/** Full guided flow: check → fix → sync. Called from check, fix, and menu. */
export async function runGuidedFlow(
  config: CaiConfig,
  opts: { includeWarnings?: boolean; verbose?: boolean } = {}
): Promise<void> {
  console.log();
  console.log(chalk.dim("  Checking if your .cai/ docs are still in sync with your code..."));
  console.log();

  const report = await runDriftCheck(config, { verbose: opts.verbose });
  const errors = report.issues.filter((i) => i.severity === "error").length;
  const warnings = report.issues.filter((i) => i.severity === "warning").length;

  // Perfect score — nothing to do
  if (report.score === 100) {
    console.log(chalk.green(`  ✓ Score: 100/100 — scaffold is perfectly in sync.`));
    console.log();
    return;
  }

  // Show the score and what's wrong
  console.log(`  ${chalk.bold("Drift score:")} ${scoreColor(report.score)(`${report.score}/100`)}`);
  console.log();

  if (errors > 0) {
    console.log(chalk.dim(`  Some scaffold files have fallen out of sync with the codebase.`));
    console.log(chalk.dim(`  ${errors} error${errors !== 1 ? "s" : ""}${warnings > 0 ? ` + ${warnings} warning${warnings !== 1 ? "s" : ""}` : ""} detected across ${countAffectedFiles(report)} file${countAffectedFiles(report) !== 1 ? "s" : ""}.`));
  } else {
    console.log(chalk.dim(`  ${warnings} warning${warnings !== 1 ? "s" : ""} — scaffold may be slightly behind the codebase.`));
  }

  console.log();
  reportConsole(report, { verbose: opts.verbose });

  if (errors === 0 && !opts.includeWarnings) {
    console.log(chalk.dim("  Run cai sync --warnings to address warnings."));
    console.log();
    return;
  }

  // Step 1: Offer auto-fix
  const wantFix = await ask(
    `  ${chalk.cyan("?")} Run safe auto-fixes first? ${chalk.dim("[Y/n]")} `
  );

  let afterFixReport: DriftReport | undefined;

  if (wantFix) {
    console.log();
    const fixResult = await runAutoFix(config);
    printFixResult(fixResult);

    if (fixResult.remainingIssueCount === 0) {
      console.log(chalk.green("  ✓ All issues resolved by auto-fix."));
      console.log();
      return;
    }

    // Re-check after fix (incremental — only re-scan changed files)
    const afterFix = await runDriftCheck(config, { incremental: true });
    afterFixReport = afterFix;
    const stillErrors = afterFix.issues.filter((i) => i.severity === "error").length;

    if (stillErrors === 0 && !opts.includeWarnings) {
      console.log(chalk.green("  ✓ All errors resolved."));
      console.log();
      return;
    }

    console.log(chalk.dim(`  ${afterFix.issues.length} issue${afterFix.issues.length !== 1 ? "s" : ""} remain that need AI help.`));
    console.log();
  }

  // Step 2: Offer AI sync
  const wantSync = await ask(
    `  ${chalk.cyan("?")} Let AI fix the rest? ${chalk.dim("[Y/n]")} `
  );

  if (!wantSync) {
    console.log();
    console.log(chalk.dim("  No problem. Run cai sync --export to get prompt files you can paste manually."));
    console.log(chalk.dim("  Note: AI sync updates your .cai/ docs to reflect current code — it never modifies source files."));
    console.log();
    return;
  }

  console.log();
  await runSync(config, { includeWarnings: opts.includeWarnings, initialReport: afterFixReport });
}

/** Interactive menu — entry point for `cai menu` */
export async function runMenu(config: CaiConfig): Promise<void> {
  const b = chalk.bold;
  const dim = chalk.dim;
  const cyan = chalk.cyan;

  // Always show current score first
  console.log();
  console.log(dim("  Checking current drift score..."));
  const report = await runDriftCheck(config);
  const errors = report.issues.filter((i) => i.severity === "error").length;
  const warnings = report.issues.filter((i) => i.severity === "warning").length;

  console.log();
  console.log(`  ${b("cai")} ${dim("· Coherence AI")}`);
  console.log();
  console.log(`  Drift score: ${scoreColor(report.score)(b(`${report.score}/100`))}  ${dim(`·  ${errors} error${errors !== 1 ? "s" : ""}, ${warnings} warning${warnings !== 1 ? "s" : ""}`)}`);
  console.log();

  if (report.score === 100) {
    console.log(cyan("  ─── What do you want to do? ─────────────────────────────"));
  } else {
    console.log(dim(`  ${errors > 0 ? "Scaffold has drifted — some files no longer match the codebase." : "Scaffold has minor drift — may be slightly behind."}`));
    console.log();
    console.log(cyan("  ─── What do you want to do? ─────────────────────────────"));
  }

  console.log();
  console.log(dim("  Fix drift"));
  console.log(`  ${cyan("1")}  ${b("Auto-fix everything")}     ${dim("· safe fixes + AI sync in one go")}`);
  console.log(`  ${cyan("2")}  ${b("Safe fixes only")}         ${dim("· deterministic repairs, no AI")}`);
  console.log(`  ${cyan("3")}  ${b("AI sync")}                 ${dim("· let Claude fix remaining drift")}`);
  console.log(`  ${cyan("4")}  ${b("View issues")}             ${dim("· full drift report")}`);
  console.log();
  console.log(dim("  Verify (back-pressure)"));
  console.log(`  ${cyan("5")}  ${b("cai verify")}              ${dim("· typecheck + build + drift")}`);
  console.log();
  console.log(dim("  Insights"));
  console.log(`  ${cyan("6")}  ${b("Health report")}           ${dim("· freshness, coverage, hot files")}`);
  console.log(`  ${cyan("7")}  ${b("Drift trend (history)")}   ${dim("· score over time + sparkline")}`);
  console.log(`  ${cyan("8")}  ${b("Telemetry stats")}         ${dim("· what Claude actually queries")}`);
  console.log();
  console.log(dim("  Learning"));
  console.log(`  ${cyan("9")}  ${b("Review corrections")}      ${dim("· recurring corrections you give Claude")}`);
  console.log(`  ${cyan("10")} ${b("Suggest patterns")}        ${dim("· library matches for this project")}`);
  console.log(`  ${cyan("11")} ${b("Recurring tasks")}         ${dim("· auto-draft patterns from commit history")}`);
  console.log();
  console.log(`  ${cyan("0")}  ${b("Exit")}`);
  console.log();

  const rl = createInterface({ input: process.stdin, output: process.stdout });
  const choice = await new Promise<string>((resolve) => {
    rl.question("  Choice [0-11]: ", (answer) => {
      rl.close();
      resolve(answer.trim());
    });
  });

  console.log();

  switch (choice) {
    case "1":
      await runGuidedFlow(config);
      break;
    case "2": {
      const result = await runAutoFix(config);
      printFixResult(result);
      break;
    }
    case "3":
      await runSync(config, {});
      break;
    case "4":
      reportConsole(report, { verbose: true });
      break;
    case "5": {
      const { runVerify, printVerifyResult } = await import("./verify.js");
      const result = await runVerify(config);
      printVerifyResult(result);
      break;
    }
    case "6": {
      const health = await runHealth(config);
      printHealth(health);
      break;
    }
    case "7": {
      const { readHistory, summarizeHistory } = await import("./drift/history.js");
      const entries = readHistory(config.projectRoot);
      const summary = summarizeHistory(entries);
      if (!summary) {
        console.log(dim(`  No drift history yet. Run cai check a few times to build a trend.`));
      } else {
        const sColor = (s: number) => s >= 80 ? chalk.green : s >= 50 ? chalk.yellow : chalk.red;
        console.log(`  Current: ${sColor(summary.current)(`${summary.current}/100`)}  ${dim(`avg ${summary.average} · best ${summary.best} · worst ${summary.worst}`)}`);
        console.log(`  Trend:   ${cyan(summary.sparkline)}  ${dim("(last 30 runs)")}`);
      }
      console.log();
      break;
    }
    case "8": {
      const { readQueries, aggregateByFile, aggregateByTool } = await import("./telemetry/query-log.js");
      const sinceMs = Date.now() - 7 * 24 * 60 * 60 * 1000;
      const queries = readQueries(config.projectRoot, { sinceMs });
      if (queries.length === 0) {
        console.log(dim(`  No MCP queries in the last 7 days. Telemetry starts when Claude calls the cai MCP server.`));
      } else {
        console.log(`  ${queries.length} queries in the last 7 days`);
        const tools = aggregateByTool(queries).slice(0, 5);
        for (const t of tools) {
          console.log(`    ${cyan(t.tool.padEnd(28))} ${t.hits}`);
        }
        const files = aggregateByFile(queries).slice(0, 5);
        if (files.length > 0) {
          console.log();
          console.log(dim("  Hot files:"));
          for (const f of files) {
            console.log(`    ${cyan(f.file.padEnd(40))} ${f.hits} hits`);
          }
        }
      }
      console.log();
      break;
    }
    case "9": {
      const { readPrompts } = await import("./learn/recorder.js");
      const { detectCorrections, clusterCorrections, suggestRule } = await import("./learn/corrections.js");
      const sinceMs = Date.now() - 14 * 24 * 60 * 60 * 1000;
      const prompts = readPrompts(config.projectRoot, { sinceMs });
      if (prompts.length === 0) {
        console.log(dim(`  No recorded prompts. Run ${chalk.white("cai learn enable")} to start recording.`));
      } else {
        const clusters = clusterCorrections(detectCorrections(prompts));
        if (clusters.length === 0) {
          console.log(dim(`  ${prompts.length} prompts recorded · 0 recurring corrections yet.`));
        } else {
          console.log(`  ${clusters.length} recurring correction${clusters.length !== 1 ? "s" : ""}:`);
          for (const c of clusters.slice(0, 5)) {
            console.log(`    ${cyan(c.id)}  ${chalk.green(`${c.count}×`)}  ${c.example}`);
            console.log(`            ${dim("→ " + suggestRule(c))}`);
          }
          console.log();
          console.log(dim(`  Apply one with: ${chalk.white("cai learn write-rule <id>")}`));
        }
      }
      console.log();
      break;
    }
    case "10": {
      const { findMatching } = await import("./pattern/matching.js");
      const { scanProjectModel } = await import("./scanner/manifest.js");
      const project = scanProjectModel(config.projectRoot);
      const matches = findMatching(project);
      if (matches.length === 0) {
        console.log(dim(`  No matching patterns in your library. Share patterns with: ${chalk.white("cai pattern share <name>")}`));
      } else {
        console.log(`  ${matches.length} matching pattern${matches.length !== 1 ? "s" : ""}:`);
        for (const m of matches.slice(0, 5)) {
          console.log(`    ${cyan(m.entry.hash)}  ${b(m.entry.name)}  ${chalk.green(`score ${m.score}`)}`);
          console.log(`            ${dim(m.entry.description)}`);
        }
      }
      console.log();
      break;
    }
    case "11": {
      const { readRecentCommits, clusterCommits } = await import("./pattern/cluster.js");
      const commits = readRecentCommits(config.projectRoot);
      const clusters = clusterCommits(commits);
      if (clusters.length === 0) {
        console.log(dim(`  No recurring task types in the last 30 days.`));
      } else {
        console.log(`  ${clusters.length} recurring task type${clusters.length !== 1 ? "s" : ""}:`);
        for (const c of clusters) {
          console.log(`    ${cyan(c.taskType.padEnd(20))} ${chalk.green(`${c.commits.length}×`)}`);
        }
        console.log();
        console.log(dim(`  Draft pattern files with: ${chalk.white("cai pattern recurring --write")}`));
      }
      console.log();
      break;
    }
    case "0":
    default:
      console.log(dim("  Bye. Run cai anytime to come back."));
      console.log();
      break;
  }
}

function countAffectedFiles(report: DriftReport): number {
  return new Set(report.issues.map((i) => i.file)).size;
}
